import React, { useState, useRef, useEffect, lazy } from 'react';
import { Link } from 'react-router-dom';
import './global-stats.scss';

// Import optimized image component (will be created)
const OptimizedImage = lazy(() => import('../common/OptimizedImage'));

// Lazy loading hook for images
const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const [hasIntersected, setHasIntersected] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasIntersected) {
          setIsIntersecting(true);
          setHasIntersected(true);
        }
      },
      { threshold: 0.1, ...options }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasIntersected, options]);

  return [ref, isIntersecting || hasIntersected];
};

// Lazy Image component
const LazyOptimizedImage = ({ src, alt, className, ...props }) => {
  const [imageRef, isVisible] = useIntersectionObserver();
  
  return (
    <div ref={imageRef} className={className}>
      {isVisible ? (
        <React.Suspense fallback={<div className="image-placeholder">Loading...</div>}>
          <OptimizedImage 
            src={src}
            alt={alt}
            className={className}
            loading="lazy"
            {...props}
          />
        </React.Suspense>
      ) : (
        <div className="image-placeholder" style={{ height: '200px', backgroundColor: '#f0f0f0' }}>
          <span>Loading...</span>
        </div>
      )}
    </div>
  );
};

const GlobalStats = () => {
  // State to track which testimonial is active
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  
  // Sample testimonials data - this could be moved to a separate file or fetched from an API
  const testimonials = [
    {
      quote: "Companies with comprehensive AI training programmes report 29% higher success rates",
      author: "AI Training Report",
      company: "ProfileTree"
    },
    {
      quote: "78% of global companies report using AI, and 71% report using generative AI",
      author: "AI Overview Report 2025",
      company: "Google"
    },
    {
      quote: "AI training can boost workforce productivity by up to 37% in some industries",
      author: "AI Training Report",
      company: "ProfileTree"
    }
    
  ];
  
  // Function to handle image loading errors
  const handleImageError = (e) => {
    // Fall back to a placeholder image if the actual image fails to load
    e.target.src = 'https://via.placeholder.com/400x300?text=Training+Image';
  };
  
  // Function to cycle through testimonials
  const nextTestimonial = () => {
    setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
  };
  
  const prevTestimonial = () => {
    setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };
  
  return (
    <div className="global-stats-container" id="global-stats">
      <div className="global-stats-content">
        <div className="global-stats-header">
          <h2>Training the <span className="highlight">Next Generation</span> of Tech Professionals around the world.</h2>
          <p>
            Our courses and consulting services are designed to empower professionals with the skills needed to excel in today's rapidly evolving technology landscape.
          </p>
        </div>
        
        <div className="stats-grid">
          <div className="stat-item">
            <div className="stat-number">20+ Years</div>
            <div className="stat-divider"></div>
            <div className="stat-description">
              Our consultants have experience delivering technical training and consultancy for the world's largest organisations.
            </div>
          </div>
          
          <div className="stat-item">
            <div className="stat-number">100+ Courses</div>
            <div className="stat-divider"></div>
            <div className="stat-description">
              Our courses are curated and developed by expert technical experts and are ready to transform your teams.
            </div>
          </div>
          
          <div className="stat-item">
            <div className="stat-number">10+ Countries</div>
            <div className="stat-divider"></div>
            <div className="stat-description">
              Our consultants are based all over the world and have experience delivering global programs.
            </div>
          </div>
          
          <div className="stat-item">
            <div className="stat-number">100k+ Trained</div>
            <div className="stat-divider"></div>
            <div className="stat-description">
              Our consultants have trained students across multiple sectors and countries across the world.
            </div>
          </div>
        </div>
        
        <div className="cta-button-container">
          <Link to="/training" className="cta-button">
            <span>Explore Our Courses</span>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>
      </div>
      
      <div className="global-stats-images">
        <div className="images-grid">          {/* Left column images - using optimized images */}          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image1" 
            alt="Professional training" 
            className="img-1" 
            onError={handleImageError}
          />
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image8" 
            alt="Software development" 
            className="img-8" 
            onError={handleImageError}
          />
          
          {/* Left middle column images - using optimized images */}
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image2" 
            alt="Team collaboration" 
            className="img-2" 
            onError={handleImageError}
          />
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image3" 
            alt="Technical training" 
            className="img-3" 
            onError={handleImageError}
          />
          
          {/* Testimonial section placed within the grid */}
          <div className="testimonial-section">
            <h3>Why AI Training Matters Now</h3>
            <div className="testimonial-carousel">
              <div className="testimonial-content">
                <blockquote>{testimonials[activeTestimonial].quote}</blockquote>
                <div className="testimonial-author">
                  <span className="author-name">{testimonials[activeTestimonial].author}</span>
                  <span className="author-company">{testimonials[activeTestimonial].company}</span>
                </div>
              </div>
              <div className="testimonial-controls">
                <button onClick={prevTestimonial} aria-label="Previous testimonial">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                <div className="testimonial-indicators">
                  {testimonials.map((_, index) => (
                    <button 
                      key={index} 
                      className={`indicator ${index === activeTestimonial ? 'active' : ''}`}
                      onClick={() => setActiveTestimonial(index)}
                      aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
                <button onClick={nextTestimonial} aria-label="Next testimonial">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          </div>
            {/* Center column image - using optimized images */}
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image4" 
            alt="Professional development" 
            className="img-4" 
            onError={handleImageError}
          />
          
          {/* Right middle column images - using optimized images */}
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image5" 
            alt="Learning environment" 
            className="img-5" 
            onError={handleImageError}
          />
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image6" 
            alt="Technology training" 
            className="img-6" 
            onError={handleImageError}
          />
          
          {/* Right column image - using optimized images */}
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image7" 
            alt="Team meeting" 
            className="img-7" 
            onError={handleImageError}
          />
          
          {/* Additional images for tablet/desktop - using optimized images */}
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image9" 
            alt="Collaborative learning" 
            className="img-9" 
            onError={handleImageError}
          />
          <LazyOptimizedImage 
            src="/assets/images/optimized/Training/image10" 
            alt="Technology workshop" 
            className="img-10" 
            onError={handleImageError}
          />
        </div>
      </div>
    </div>
  );
};

export default GlobalStats;
